import { useEffect, useRef, useState } from 'react'
import { Button, ExplainerBanner, OptionGrid, PhotoAttach, Skeleton, Textarea } from '@/ds'
import { NotAvailable } from '@/app/components/NotAvailable'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { peticionesService } from '@/services/peticiones'
import type { EntryPreference, GuestRequestFormType } from '@/services/peticiones'
import { useSave } from '../Checkin/useSave'
import { BackLink } from '../Pagos/shared'
import { BOOKING_ROUTES, changeQuery, useLang } from '../Reserva/shared'
import { HELP_ROUTES } from './shared'

type FormType = GuestRequestFormType | 'late_checkout'
const TYPES: FormType[] = ['towels_amenities', 'cleaning', 'issue', 'lost_item', 'late_checkout', 'other']
/** Tipos en los que alguien entra en el alojamiento: se pregunta cuándo viene bien. */
const WITH_ENTRY: FormType[] = ['towels_amenities', 'cleaning', 'issue']
const ENTRY: EntryPreference[] = ['anytime', 'only_when_present']

interface Photo {
  id: string
  file: File
  url: string | undefined
}

/**
 * 8.8 · Nueva petición (Servicios.dc.html request, Bloque 6 §6e). Formulario corto, no chat: tipo en rejilla, qué
 * pasa, foto opcional y cuándo pueden entrar. "Salir más tarde" no es una petición: lleva al cambio de reserva (D47).
 * Sin peticiones en la API actual (D48): WhatsApp con la referencia y teléfono.
 */
export function NuevaPeticion() {
  const { t } = useLang()
  const stay = useLoadedStay()
  const [requests] = useAsync(`peticiones:${stay.guestCode}`, (signal) => peticionesService.list(stay, signal))
  const unsupported = requests.kind === 'unsupported'
  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to={HELP_ROUTES.home} label={t('v2.peticiones.nueva.volver')} />
        <h1>{t('v2.peticiones.nueva.titulo')}</h1>
        {!unsupported && <p className="page__lead">{t('v2.peticiones.nueva.lead')}</p>}
      </div>
      {requests.kind === 'loading' ? (
        <div aria-busy="true">
          <Skeleton height={320} />
        </div>
      ) : unsupported ? (
        <NotAvailable />
      ) : (
        <RequestForm />
      )}
    </main>
  )
}

function RequestForm() {
  const { t } = useLang()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  const [type, setType] = useState<FormType | null>(null)
  const [text, setText] = useState('')
  const [entry, setEntry] = useState<EntryPreference>('anytime')
  const [photos, setPhotos] = useState<Photo[]>([])
  const [showErrors, setShowErrors] = useState(false)
  const { saving, error, run } = useSave()
  const urls = useRef<string[]>([])
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), [])

  const addPhoto = (file: File) => {
    const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined
    if (url) urls.current.push(url)
    setPhotos((p) => [...p, { id: `${Date.now()}-${p.length}`, file, url }])
  }

  const typeError = showErrors && !type ? t('v2.peticiones.nueva.tipoError') : null
  const textError = showErrors && !text.trim() ? t('v2.peticiones.nueva.textoError') : null

  const submit = async () => {
    setShowErrors(true)
    if (!type || type === 'late_checkout' || !text.trim()) return
    const created = await run(() => peticionesService.create(stay, { type, text: text.trim(), photos: photos.map((p) => p.file), entryPreference: WITH_ENTRY.includes(type) ? entry : null }))
    if (created) go(HELP_ROUTES.request(created.id), { replace: true })
  }

  return (
    <form
      className="pagos__stack"
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <div className="pagos__group">
        <OptionGrid label={t('v2.peticiones.nueva.tipo')} options={TYPES.map((id) => ({ id, label: t(`v2.peticiones.tipo.${id}`) }))} value={type} onChange={(id) => setType(id as FormType)} />
        {typeError && (
          <p className="pagos__error" role="alert">
            {typeError}
          </p>
        )}
      </div>

      {type === 'late_checkout' ? (
        <>
          <ExplainerBanner title={t('v2.peticiones.tipo.late_checkout')}>{t('v2.peticiones.nueva.salirTexto')}</ExplainerBanner>
          <Button type="button" onClick={() => go(BOOKING_ROUTES.pick('late_checkout'), { query: changeQuery('peticion') })}>
            {t('v2.peticiones.nueva.salirBoton')}
          </Button>
        </>
      ) : (
        <>
          <Textarea label={t('v2.peticiones.nueva.texto')} rows={3} value={text} onChange={(e) => setText(e.target.value)} help={t('v2.peticiones.nueva.textoAyuda')} error={textError ?? undefined} required />
          <PhotoAttach
            label={t('v2.peticiones.nueva.foto')}
            labelNote={t('v2.peticiones.nueva.fotoNota')}
            photos={photos.map((p, i) => ({ id: p.id, ...(p.url ? { url: p.url } : {}), label: t('v2.peticiones.nueva.fotoN', { n: i + 1 }) }))}
            onAdd={addPhoto}
            onRemove={(id) => setPhotos((list) => list.filter((p) => p.id !== id))}
          />
          {type && WITH_ENTRY.includes(type) && (
            <OptionGrid label={t('v2.peticiones.nueva.entrar')} options={ENTRY.map((id) => ({ id, label: t(`v2.peticiones.nueva.entrada.${id}`) }))} value={entry} onChange={(id) => setEntry(id as EntryPreference)} />
          )}
          <div className="pagos__group">
            <Button type="submit" loading={saving}>
              {t('v2.peticiones.nueva.enviar')}
            </Button>
            {error && (
              <p className="pagos__error" role="alert">
                {error}
              </p>
            )}
            <p className="pagos__note">{t('v2.peticiones.nueva.nota')}</p>
          </div>
        </>
      )}
    </form>
  )
}
